import { useCallback, useEffect, useRef } from 'react'

import { getMe } from '@/api/user'
import { getSubscription, recoverAppleIAP, verifyAppleIAP } from '@/api/pay'
import { applyMembershipSnapshot, useAppStore } from '@/hooks/useAppStore'
import { useNetworkStatus } from '@/hooks/useNetworkStatus'
import { isNativeApp } from '@/utils/platform'
import { entitlementRefreshDelay } from '@/utils/iapLifecycle'
import { recoverUnfinishedAppleTransactions } from '@/pages/membership/appleIapRecovery'
import {
  clearPendingApplePaymentContext,
  clearPendingPaymentContext,
  loadPendingApplePaymentContext,
  pendingPaymentBelongsTo,
} from '@/utils/paymentContext'

let recoveryInFlight: Promise<void> | null = null

async function refreshEntitlementSnapshot(): Promise<void> {
  const [me, subscription] = await Promise.all([getMe(), getSubscription()])
  applyMembershipSnapshot(subscription, me.points)
}

async function recoverAndRefresh(userId: string): Promise<void> {
  if (recoveryInFlight) return recoveryInFlight
  recoveryInFlight = (async () => {
    if (!pendingPaymentBelongsTo(userId)) clearPendingPaymentContext()

    const bridge = window.__NATIVE_BRIDGE__
    if (isNativeApp() && bridge?.getUnfinishedTransactions && bridge.finishTransaction) {
      await recoverUnfinishedAppleTransactions({
        bridge: {
          getUnfinishedTransactions: bridge.getUnfinishedTransactions,
          finishTransaction: bridge.finishTransaction,
        },
        pendingContext: loadPendingApplePaymentContext(),
        verify: verifyAppleIAP,
        recover: recoverAppleIAP,
        onConfirmed: (orderId) => {
          if (orderId) clearPendingApplePaymentContext(orderId)
        },
        onTransactionError: (error) => {
          console.warn('[iap] unfinished transaction awaits its bound account/server', error)
        },
      })
    }
    await refreshEntitlementSnapshot()
  })().finally(() => {
    recoveryInFlight = null
  })
  return recoveryInFlight
}

export default function IapLifecycleCoordinator() {
  const { user, authInitialized } = useAppStore()
  const userId = user?.userId
  const isOnline = useNetworkStatus()
  const timerRef = useRef<number | null>(null)

  const synchronize = useCallback(() => {
    if (!authInitialized || !userId || isOnline === false) return
    void recoverAndRefresh(userId).catch((error) => {
      console.warn('[iap] lifecycle synchronization failed', error)
    })
  }, [authInitialized, isOnline, userId])

  useEffect(() => {
    synchronize()
  }, [synchronize])

  useEffect(() => {
    const bridge = window.__NATIVE_BRIDGE__
    if (!authInitialized || !userId || !bridge?.addAppleIapTransactionListener) return
    let disposed = false
    let removeListener: (() => Promise<void>) | null = null
    void bridge.addAppleIapTransactionListener(() => synchronize()).then((remove) => {
      if (disposed) void remove()
      else removeListener = remove
    })
    return () => {
      disposed = true
      if (removeListener) void removeListener()
    }
  }, [authInitialized, synchronize, userId])

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') synchronize()
    }
    const onOnline = () => synchronize()
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('online', onOnline)
    return () => {
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('online', onOnline)
    }
  }, [synchronize])

  useEffect(() => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current)
    timerRef.current = null
    const delay = entitlementRefreshDelay(user?.vipEntitlement)
    if (delay === null) return
    timerRef.current = window.setTimeout(synchronize, delay)
    return () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current)
    }
  }, [synchronize, user?.vipEntitlement])

  return null
}
